import { ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { CustomerBillingInvoiceModel } from '../customer-billing-invoice.model';
import { CustomerBillingCustomerModel } from '../../customer-billing-customer/customer-billing-customer.model';
import { AppTextService } from '@fi-shared/app-text';
import { InvoiceType, InvoiceTypes } from '../invoice-type';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { DropdownSettingsModelV2 } from '@fi-shared/dropdown';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { InvoiceService } from '../invoice.service';
import { CustomerService } from '../../customer-billing-customer/customer.service';
import { fiCustomerPaymentStatus } from '../../customer-billing-customer/customer-payment-status';
import { fiCustomerType } from '../../customer-billing-customer/customer-type';
import { ObjectTitleService } from '@fi-app/utilities/fi-services/object-title.service';
import { EditTabModel, EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { CustomerBillingInvoiceDetailModel } from '@fi-app/customer-billing/customer-billing-invoice/customer-billing-invoice-detail.model';
import { CustomerBillingOtherCustomerModel } from '@fi-app/customer-billing/customer-billing-other-customer/customer-billing-other-customer.model';
import { createMap } from '@fi-shared/utils/create-map';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';

@Component({
    selector: 'fi-customer-billing-invoice-edit-state',
    templateUrl: './customer-billing-invoice-edit-state.component.html',
    styleUrls: ['./customer-billing-invoice-edit-state.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-customer-billing-invoice-edit-state' },
    standalone: false,
})
export class CustomerBillingInvoiceEditStateComponent implements OnInit {
    @Input() invoice: CustomerBillingInvoiceModel;
    @Input() customers: CustomerBillingCustomerModel[];

    CustomersListData = new DropdownSettingsModelV2<CustomerBillingCustomerModel>({
        dataValueField: 'BillingCustomerId',
    });
    //InvoiceDetailTypes: CustomerBillingInvoiceDetailModel = CustomerBillingInvoiceDetailModel;
    InvoiceTypes = InvoiceTypes;
    ObjectTypes = ObjectTypes;
    EditTabs: EditTabsModel;
    private customersMap: { [key: string]: any };

    constructor(
        private appTextService: AppTextService,
        private customerService: CustomerService,
        private invoiceService: InvoiceService,
        private fiLoadingService: LoadingService,
        private stateService: FiStateService,
        private objectTitleService: ObjectTitleService,
        private customerDataService: FiCustomerDataService,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    ngOnInit() {
        this.setPageHeaderData();
        this.setCustomerListData();
        this.onInvoiceTypeChange(this.invoice.InvoiceType);
    }

    private setPageHeaderData() {
        this.EditTabs = new EditTabsModel(this.getPageHeaderTitle());
        this.EditTabs.Tabs.push(
            new EditTabModel(this.appTextService.get('common.general'), this.appTextService.get('common.general')),
        );
    }

    private setCustomerListData() {
        this.customersMap = createMap(this.customers, 'BillingCustomerId');

        this.invoice.setOwnerInformation(
            createMap(this.customers, 'CustomerId')[this.customerDataService.customerData.CustomerId],
        );

        this.CustomersListData.setDataSet(this.customers);

        if (this.invoice.Id) {
            //$scope.ShowPage = $scope.PageSections[$scope.PageSections.length - 1].key;

            this.setupCustomerFullInfo(
                this.invoice.BillingCustomerId,
                (customer: CustomerBillingOtherCustomerModel) => {
                    this.invoice.setCustomerReferences(customer, this.invoice.CustomerReference);
                },
            );
        }
    }

    getPageHeaderTitle(): string {
        if (this.invoice.Id)
            return this.objectTitleService.getEditTitle(
                `${this.appTextService.get('common.invoice')} ${this.invoice.Id} - ${this.invoice.CustomerName}`,
            );

        return this.objectTitleService.getCreateTitle(ObjectTypes.Invoice);
    }

    private SetupInvoiceArticles(billingCustomerId: number, invoiceType: InvoiceType) {
        if (billingCustomerId && invoiceType === InvoiceType.Monthly) {
            this.invoiceService
                .GetMonthlyInvoicePredefinedArticles(billingCustomerId)
                .then((articles: CustomerBillingInvoiceDetailModel[]) => {
                    this.invoice.setupInitialMonthlyArticles(articles, this.appTextService, this.fiFormatDateService);
                });
        } else {
            this.invoice.clearPriceArticles();
        }
    }

    private setupCustomerFullInfo(
        billingCustomerId: number,
        callback: (value: CustomerBillingOtherCustomerModel) => void,
    ) {
        const selectedCustomer = this.customersMap[billingCustomerId];
        if (!selectedCustomer.InvoiceInfo) {
            this.fiLoadingService
                .trackPromise(this.customerService.GetOtherBillingCustomer(selectedCustomer.CustomerId))
                .then((response: CustomerBillingOtherCustomerModel) => {
                    this.customersMap[billingCustomerId] = response;
                    callback(response);
                });
        } else {
            callback(selectedCustomer);
        }
    }

    onBillingCustomerIdChange(newBillingCustomerId: number) {
        if (this.invoice.Id) return;

        if (newBillingCustomerId) {
            this.setupCustomerFullInfo(newBillingCustomerId, (customer: CustomerBillingOtherCustomerModel) => {
                this.invoice.setCustomerInformation(customer);
            });
        } else {
            this.invoice.clearCustomerInformation();
        }

        this.SetupInvoiceArticles(newBillingCustomerId, this.invoice.InvoiceType);
    }

    onInvoiceTypeChange(newInvoiceType: InvoiceType) {
        if (this.invoice.Id) return;

        this.invoice.BillingCustomerId = null;

        this.CustomersListData.setDataSet(
            this.customers.filter((value: CustomerBillingCustomerModel) => {
                return (
                    newInvoiceType == InvoiceType.Other ||
                    (value.CustomerType == fiCustomerType.Intranet &&
                        value.PaymentStatus == fiCustomerPaymentStatus.Paying)
                );
            }),
        );

        this.onBillingCustomerIdChange(this.invoice.BillingCustomerId);
    }

    save() {
        this.fiLoadingService.trackPromise(this.invoiceService.SaveInvoice(this.invoice)).then(() => {
            this.stateService.go(FiStateNames.CustomerBilling.Invoice.List);
        });
    }
}
